import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, SearchLg } from '../../assets';
import { Franchise, Store } from '../../app/services/api/generated';
import { ListPageTemplate } from '../../components/page-templates/list-page/ListPageTemplate';
import { ConfirmationDialog } from '../../components/shared/dialogs/ConfirmationDialog';
import { EmptyTable } from '../../components/shared/EmptyTable';
import MessagesSnackbar from '../../components/shared/MessagesSnackbar';
import { useConfigurations } from '../../components/shared/useConfigurations';
import { UserContext } from '../../components/shared/useUser';
import useLogError from '../../hooks/useLogError';
import useNswagClient, { ManagedConfiguration, ManagedConfigurationWrite } from '../../hooks/api/useNswagClient';
import { ConfigurationScope, localisedDescription, localisedName, useConfigurationTableSection } from '../ManageConfigurations/components/ConfigurationTable';

/**
 * Customer-facing self-service. Only definitions the catalogue marks customer-manageable reach this
 * screen, and the change history lives on the Orderly-only management screen, so no audit action is
 * offered here.
 *
 * Scope follows the user's level, as on the management screen: a store user is fixed to their
 * store; a franchise user picks a store or leaves it empty to edit the franchise-wide value; an
 * Orderly administrator also picks the franchise. Franchise and store lists are fetched only when
 * their dropdown is opened. A flag the definition allows only at Franchise level is written at
 * Franchise scope with the Franchise row's revision, and is read-only for a user who has no
 * franchise or tenant access.
 */
type ScopeLevel = 'store' | 'franchise' | 'admin';

type Feedback = { message: string; severity: 'success' | 'error' | 'warning' };

type PendingChange = { item: ManagedConfiguration; value?: string; removeOverride: boolean };

const ALL = '';

const FeatureFlags = () => {
  const { t } = useTranslation('manageConfigurations');
  const { selectedStore, hasPermissionTo, isOrderlyAdmin, user } = useContext(UserContext);
  const { logError } = useLogError();
  const { invalidateConfigurations } = useConfigurations();
  const { getFeatureFlags, saveFeatureFlag, getFranchises, getFranchiseStores } = useNswagClient();

  const canManage = hasPermissionTo(['FeatureFlagsManage']);
  // UserLevel stays StoreUser for an account given franchise or tenant assignments, so scope follows
  // the access the current-user endpoint reports, not that label. Tenant access picks the franchise
  // and the store; franchise access picks the store only; store access has no picker.
  const hasTenantScope = isOrderlyAdmin() || user?.hasTenantLevelAccess === true;
  const hasFranchiseScope = user?.userLevel === 'FranchiseUser' || user?.hasFranchiseLevelAccess === true;
  const level: ScopeLevel = hasTenantScope ? 'admin' : hasFranchiseScope ? 'franchise' : 'store';

  const [franchise, setFranchise] = useState<Franchise | null>(() =>
    selectedStore?.franchiseName ? { franchiseName: selectedStore.franchiseName } as Franchise : null);
  const [store, setStore] = useState<Store | null>(() => selectedStore ?? null);
  const [franchiseOptions, setFranchiseOptions] = useState<Franchise[]>();
  const [loadingFranchises, setLoadingFranchises] = useState(false);
  const [storeOptions, setStoreOptions] = useState<{ franchiseName: string; stores: Store[] }>();
  const [loadingStores, setLoadingStores] = useState(false);

  const [items, setItems] = useState<ManagedConfiguration[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingName, setSavingName] = useState<string>();
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [pending, setPending] = useState<PendingChange>();
  const [retries, setRetries] = useState<Record<string, PendingChange>>({});
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState(ALL);

  const franchiseName = franchise?.franchiseName;
  const storeNumber = store?.storeNumber;
  const scope: ConfigurationScope = store ? 'Store' : 'Franchise';
  const canWriteAtFranchise = level !== 'store';
  const scopeFor = (item: ManagedConfiguration): ConfigurationScope =>
    store && (item.canSetAtStore || !canWriteAtFranchise) ? 'Store' : 'Franchise';

  // A save that completes after the user has moved to another scope must not touch that scope's
  // list. The generation advances on each scope change, so coming back does not revive a response
  // from before the user left.
  const generationRef = useRef(0);
  const stillIn = (generation: number) => generationRef.current === generation;

  const reportError = (reason: unknown, message: string) => {
    // An Axios error carries the request body; never log it raw.
    logError(axios.isAxiosError(reason) ? { status: reason.response?.status, url: reason.config?.url, message: reason.message } : reason);
    setFeedback({ message, severity: 'error' });
  };

  const isConflict = (reason: unknown) => axios.isAxiosError(reason) && reason.response?.status === 409;

  // The store the user works in elsewhere in the app is the starting point here too.
  useEffect(() => {
    setFranchise(selectedStore?.franchiseName ? { franchiseName: selectedStore.franchiseName } as Franchise : null);
    setStore(selectedStore ?? null);
  }, [selectedStore?.franchiseName, selectedStore?.storeNumber]);

  // Lists are fetched when a dropdown is opened, not when the page loads.
  const openFranchises = () => {
    if (franchiseOptions || loadingFranchises) return;
    setLoadingFranchises(true);
    void getFranchises()
      .then(result => setFranchiseOptions(result.data ?? []))
      .catch(reason => reportError(reason, t('featureFlags.loadError')))
      .finally(() => setLoadingFranchises(false));
  };

  const openStores = () => {
    if (!franchiseName || loadingStores || storeOptions?.franchiseName === franchiseName) return;
    setLoadingStores(true);
    const forFranchise = franchiseName;
    void getFranchiseStores(forFranchise)
      .then(result => setStoreOptions({ franchiseName: forFranchise, stores: result.data?.stores ?? [] }))
      .catch(reason => reportError(reason, t('featureFlags.loadError')))
      .finally(() => setLoadingStores(false));
  };

  // Until a list is loaded, the current value is the only option, so the field can show it.
  const withCurrent = <T,>(options: T[] | undefined, current: T | null): T[] =>
    options ? options : current ? [current] : [];
  const franchiseChoices = withCurrent(franchiseOptions, franchise);
  const storeChoices = withCurrent(storeOptions && storeOptions.franchiseName === franchiseName ? storeOptions.stores : undefined, store);

  // The server refused a stale revision: show what it holds now so the next save starts from it.
  const reloadAfterConflict = async (reason: unknown, startedIn: number) => {
    if (!stillIn(startedIn)) return;
    reportError(reason, t('conflictError'));
    setRetries({});
    if (!franchiseName) return;
    try {
      const reloaded = await getFeatureFlags(franchiseName, storeNumber);
      if (!stillIn(startedIn)) return;
      setItems(reloaded);
      clearDrafts();
    } catch (loadFailure) {
      logError(loadFailure);
    }
  };

  const categories = useMemo(
    () => Array.from(new Set(items.map(item => item.category))).sort((left, right) => left.localeCompare(right)),
    [items],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter(item => {
      if (category !== ALL && item.category !== category) return false;
      return !term || `${localisedName(t, item)} ${localisedDescription(t, item)} ${item.category}`.toLowerCase().includes(term);
    });
  }, [items, search, category, t]);

  const refreshAfterSave = async (change: PendingChange, startedIn: number) => {
    try {
      await invalidateConfigurations();
    } catch (reason) {
      logError(reason);
      if (!stillIn(startedIn)) return;
      setRetries(current => ({ ...current, [change.item.name]: change }));
      setFeedback({ message: t('refreshWarning'), severity: 'warning' });
    }
  };

  const save = async ({ item, value, removeOverride }: PendingChange) => {
    if (!franchiseName) return;
    setSavingName(item.name);
    const startedIn = generationRef.current;

    const atStore = scopeFor(item) === 'Store';
    const request: ManagedConfigurationWrite = {
      name: item.name,
      franchiseName,
      storeNumber: atStore ? storeNumber : undefined,
      value,
      expectedRevision: atStore ? item.revision : item.franchiseRevision,
      removeOverride,
    };

    try {
      const saved = await saveFeatureFlag(request);
      if (!stillIn(startedIn)) return;
      setItems(current => current.map(flag => flag.name === saved.item.name ? saved.item : flag));
      clearDraft(item.name);
      setRetries(current => {
        const next = { ...current };
        if (saved.warnings.length > 0) next[item.name] = { item, value, removeOverride };
        else delete next[item.name];
        return next;
      });
      setFeedback(saved.warnings.length > 0
        ? { message: `${t('propagationWarning')} ${saved.warnings.join(' ')}`, severity: 'warning' }
        : { message: t('saveSuccess', { name: localisedName(t, item) }), severity: 'success' });
      await refreshAfterSave({ item, value, removeOverride }, startedIn);
    } catch (reason) {
      if (isConflict(reason)) await reloadAfterConflict(reason, startedIn);
      else if (stillIn(startedIn)) reportError(reason, t('featureFlags.saveError'));
    } finally {
      if (stillIn(startedIn)) setSavingName(undefined);
    }
  };

  const { table, clearDraft, clearDrafts, resetPage } = useConfigurationTableSection({
    title: t('featureFlags.tableTitle'),
    scope,
    scopeFor,
    items: canManage && franchiseName ? filtered : [],
    loading,
    savingName,
    retryNames: useMemo(() => new Set(Object.keys(retries)), [retries]),
    onRetry: item => {
      const change = retries[item.name];
      if (change) void save({ ...change, item });
    },
    recordCountLabel: t('featureFlags.recordCountLabel'),
    emptyContent: franchiseName ? (
      <EmptyTable
        text={t('featureFlags.tableTitle')}
        searchTerm={search}
        onClear={() => clearFilters()}
        onAdd={() => undefined}
        addItemText=""
        onAddIsCreating={false}
        showOnAdd={false}
        withPaginationShown={false}
      />
    ) : (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 240,
          textAlign: 'center',
        }}
      >
        <Typography
          variant="textMD"
          color="text.secondary"
        >
          {t(level === 'store' ? 'featureFlags.selectStorePrompt' : 'selectFranchisePrompt')}
        </Typography>
      </Box>
    ),
    onSave: (item, value, removeOverride = false) => setPending({ item, value, removeOverride }),
  });

  const applySearch = () => {
    setSearch(searchInput.trim());
    resetPage();
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setCategory(ALL);
    resetPage();
  };

  // A response for a scope the user has since left must not be shown as the current one.
  useEffect(() => {
    generationRef.current += 1;
    clearDrafts();
    resetPage();
    setSavingName(undefined);
    setPending(undefined);
    setRetries({});
    // The previous scope's flags must never stay on screen: if this load fails they would be saved
    // against the new scope.
    setItems([]);
    if (!canManage || !franchiseName) {
      setLoading(false);
      return;
    }
    let stale = false;
    setLoading(true);
    void getFeatureFlags(franchiseName, storeNumber)
      .then(result => { if (!stale) setItems(result); })
      .catch(reason => { if (!stale) reportError(reason, t('featureFlags.loadError')); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [canManage, franchiseName, storeNumber]);

  if (!canManage) return null;

  const filterContent = (
    <Grid
      container
      spacing={5}
    >
      <Grid
        item
        lg={3}
        sm={6}
        xs={12}
      >
        <TextField
          fullWidth
          label={t('search')}
          placeholder={t('featureFlags.searchPlaceholder')}
          value={searchInput}
          disabled={!franchiseName}
          onChange={event => setSearchInput(event.target.value)}
          onKeyDown={event => event.key === 'Enter' && applySearch()}
        />
      </Grid>
      <Grid
        item
        lg={2}
        sm={3}
        xs={6}
      >
        <Button
          fullWidth
          variant="secondary"
          size="lg"
          disabled={!franchiseName}
          onClick={clearFilters}
        >
          {t('clear')}
        </Button>
      </Grid>
      <Grid
        item
        lg={2}
        sm={3}
        xs={6}
      >
        <Button
          fullWidth
          variant="primary"
          size="lg"
          disabled={!franchiseName}
          startIcon={<SvgIcon><SearchLg /></SvgIcon>}
          onClick={applySearch}
        >
          {t('search')}
        </Button>
      </Grid>
      {level === 'admin' && (
        <Grid
          item
          lg={2.5}
          sm={6}
          xs={12}
        >
          <Autocomplete
            options={franchiseChoices}
            value={franchise}
            loading={loadingFranchises}
            onOpen={openFranchises}
            onChange={(_, selected) => { setFranchise(selected); setStore(null); }}
            getOptionLabel={option => option.franchiseName ?? ''}
            isOptionEqualToValue={(option, selected) => option.franchiseName === selected.franchiseName}
            renderInput={params => (
              <TextField
                {...params}
                label={t('franchise')}
                placeholder={t('selectFranchise')}
              />
            )}
          />
        </Grid>
      )}
      {level !== 'store' && (
        <Grid
          item
          lg={2.5}
          sm={6}
          xs={12}
        >
          <Autocomplete
            options={storeChoices}
            value={store}
            loading={loadingStores}
            disabled={!franchiseName}
            onOpen={openStores}
            onChange={(_, selected) => setStore(selected)}
            getOptionLabel={option => option.storeName ?? option.storeNumber ?? ''}
            isOptionEqualToValue={(option, selected) => option.storeNumber === selected.storeNumber}
            renderInput={params => (
              <TextField
                {...params}
                label={t('storeOverrideScope')}
                placeholder={t('franchiseDefault')}
              />
            )}
          />
        </Grid>
      )}
      {franchiseName && !store && level !== 'store' && (
        <Grid
          item
          xs={12}
        >
          <Alert severity="info">{t('featureFlags.franchiseScope')}</Alert>
        </Grid>
      )}
      {!canWriteAtFranchise && items.some(item => !item.canSetAtStore) && (
        <Grid
          item
          xs={12}
        >
          <Alert severity="info">{t('featureFlags.franchiseOnlyReadOnly')}</Alert>
        </Grid>
      )}
    </Grid>
  );

  const advancedFilterContent = (
    <Grid
      container
      spacing={5}
    >
      <Grid
        item
        lg={3}
        sm={6}
        xs={12}
      >
        <FormControl fullWidth>
          <InputLabel
            id="feature-flag-category-label"
            shrink
          >
            {t('filters.category')}
          </InputLabel>
          <Select
            labelId="feature-flag-category-label"
            value={category}
            label={t('filters.category')}
            displayEmpty
            notched
            onChange={event => {
              setCategory(event.target.value);
              resetPage();
            }}
          >
            <MenuItem value={ALL}>{t('filters.allCategories')}</MenuItem>
            {categories.map(name => (
              <MenuItem
                key={name}
                value={name}
              >
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  return (
    <>
      <ListPageTemplate<ManagedConfiguration>
        title={t('featureFlags.title')}
        subtitle={t('featureFlags.subtitle')}
        filterContent={filterContent}
        advancedFilterContent={advancedFilterContent}
        advancedFilterActions={(
          <Button
            variant="secondary"
            size="md"
            onClick={clearFilters}
          >
            {t('filters.clear')}
          </Button>
        )}
        table={table}
      />
      <ConfirmationDialog
        open={Boolean(pending)}
        icon={<AlertTriangle />}
        variant="primary"
        title={pending ? t('featureFlags.confirmChangeTitle', { name: localisedName(t, pending.item) }) : ''}
        message={t('featureFlags.confirmChange')}
        confirmButtonText={t('actions.save')}
        cancelButtonText={t('cancel')}
        onConfirm={() => {
          const change = pending;
          setPending(undefined);
          if (change) void save(change);
        }}
        onCancel={() => setPending(undefined)}
      />
      <MessagesSnackbar
        open={Boolean(feedback)}
        onClose={() => setFeedback(null)}
        message={feedback?.message ?? ''}
        severity={feedback?.severity ?? 'success'}
        duration={feedback?.severity === 'success' ? 4000 : null}
      />
    </>
  );
};

export default FeatureFlags;
